import { Button, IconArrowClose } from "@studyplus/boron-ui";
import * as React from "react";
import Icon from "../../../../components/atoms/Icon";
import { Modal } from "../../../../components/atoms/Modal";
import { toJapanese } from "../../../../domains/PaidService";
import { useFlashMessage } from "../../../../hooks/useFlashMessage";
import StudentInterface from "../../../../interfaces/StudentInterface";
import { PaidServiceItem } from "./PaidServiceItem";
import styles from "./PaidServiceSettingsModal.scss";
import {
  DISABLEABLE_STATUSES,
  ENABLEABLE_STATUSES,
  getPaidServiceConfig,
} from "./paidServiceConfig";
import {
  PaidServiceAvailability,
  PaidServiceAvailabilityStatus,
  useFetchPaidServiceAvailabilities,
} from "./useFetchPaidServiceAvailabilities";
import {
  useDisablePaidService,
  useEnablePaidService,
} from "./useUpdatePaidService";

const canEnable = (status: PaidServiceAvailabilityStatus): boolean =>
  ENABLEABLE_STATUSES.includes(status);

const canDisable = (status: PaidServiceAvailabilityStatus): boolean =>
  DISABLEABLE_STATUSES.includes(status);

type ModalView =
  | { type: "list" }
  | { type: "confirmEnable"; service: PaidServiceAvailability }
  | { type: "confirmDisable"; service: PaidServiceAvailability };

type ListViewProps = {
  student: StudentInterface;
  paidServiceAvailabilities: PaidServiceAvailability[] | undefined;
  isLoading: boolean;
  onClose: () => void;
  onEnableClick: (service: PaidServiceAvailability) => void;
  onDisableClick: (service: PaidServiceAvailability) => void;
};

const ListView = ({
  student,
  paidServiceAvailabilities,
  isLoading,
  onClose,
  onEnableClick,
  onDisableClick,
}: ListViewProps) => (
  <>
    <Modal.Header onClose={onClose}>
      {student.fullName} さんの有料サービス設定
    </Modal.Header>
    <Modal.Body className="overflow-y-auto p-[24px]">
      <div className="flex flex-col gap-[0.4rem]">
        <p className="text-[1.4rem] leading-[1.5] text-black">
          標準機能に加えて、生徒ごとに必要なサービスを追加できます。追加した有料サービスごとに利用料金が発生します。
        </p>
        <a
          href="http://fs-help.studyplus.co.jp/ja/articles/13838036"
          className={styles.modalDescriptionLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon name="icon-attention" />
          各サービスの詳細や変更時の注意点はこちら
        </a>
      </div>
      <div className="mt-[2.4rem] flex flex-col gap-[1rem]">
        {isLoading ? (
          <p>読み込み中...</p>
        ) : (
          paidServiceAvailabilities?.map((service) => {
            const config = getPaidServiceConfig(service.name);
            return (
              <PaidServiceItem
                key={service.id}
                title={config?.displayName ?? toJapanese(service.name)}
                description={config?.listDescription ?? ""}
                status={service.status}
                canEnable={canEnable(service.status)}
                canDisable={canDisable(service.status)}
                trialApplicationUrl={config?.trialApplicationUrl}
                onEnable={() => onEnableClick(service)}
                onDisable={() => onDisableClick(service)}
              />
            );
          })
        )}
      </div>
    </Modal.Body>
  </>
);

type ConfirmViewProps = {
  type: "enable" | "disable";
  service: PaidServiceAvailability;
  studentName: string;
  isUpdating: boolean;
  isPending: boolean;
  onClose: () => void;
  onBack: () => void;
  onConfirm: () => void;
};

const ConfirmView = ({
  type,
  service,
  studentName,
  isUpdating,
  isPending,
  onClose,
  onBack,
  onConfirm,
}: ConfirmViewProps) => {
  const config = getPaidServiceConfig(service.name);
  const serviceName = config?.displayName ?? toJapanese(service.name);
  const description =
    type === "enable"
      ? (config?.enableDescription ?? "")
      : (config?.disableDescription ?? "");
  const features = config?.features ?? [];
  const confirmMessage =
    type === "enable"
      ? `${studentName} さんの「${serviceName}」を利用しますか？`
      : `${studentName} さんの「${serviceName}」を停止しますか？`;
  const confirmButtonText = type === "enable" ? "利用を開始" : "停止する";

  return (
    <>
      <Modal.Header onClose={onClose}>
        <button
          type="button"
          className="flex cursor-pointer items-center gap-[0.4rem] border-none bg-transparent p-0 text-[1.4rem] font-bold text-black hover:opacity-70"
          onClick={onBack}
        >
          <IconArrowClose />
          戻る
        </button>
      </Modal.Header>
      <Modal.Body className="overflow-y-auto p-[24px]">
        <div className="flex flex-col">
          <h3 className="m-0 mb-[0.4rem] text-[1.6rem] font-bold text-black">
            {serviceName}
          </h3>
          <p className="m-0 mb-[1.6rem] text-[1.4rem] leading-[1.5] text-black">
            {description}
          </p>
          <div className={styles.confirmFeatureList}>
            <ul>
              {features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          </div>
          <p className="m-0 mb-[1.6rem] text-center text-[1.4rem] font-bold leading-[1.5] text-black">
            {confirmMessage}
          </p>
          <div className="flex justify-center gap-[1.2rem]">
            <Button variant="outline" onClick={onBack} disabled={isUpdating}>
              キャンセル
            </Button>
            <Button
              onClick={onConfirm}
              disabled={isUpdating}
              isLoading={isPending}
            >
              {confirmButtonText}
            </Button>
          </div>
        </div>
      </Modal.Body>
    </>
  );
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  student: StudentInterface;
  sectionId: string;
};

export const PaidServiceSettingsModal = ({
  isOpen,
  onClose,
  student,
  sectionId,
}: Props) => {
  const [modalView, setModalView] = React.useState<ModalView>({
    type: "list",
  });
  const { showSuccessMessage, showErrorMessage } = useFlashMessage();

  const { data: paidServiceAvailabilities, isLoading } =
    useFetchPaidServiceAvailabilities({
      sectionId,
      studentId: student.id,
      enabled: isOpen && !!sectionId,
    });

  const enablePaidService = useEnablePaidService();
  const disablePaidService = useDisablePaidService();

  const isUpdating =
    enablePaidService.isPending || disablePaidService.isPending;

  React.useEffect(() => {
    if (isOpen) {
      setModalView({ type: "list" });
    }
  }, [isOpen]);

  const handleEnableClick = (service: PaidServiceAvailability) => {
    if (!canEnable(service.status)) {
      return;
    }
    setModalView({ type: "confirmEnable", service });
  };

  const handleDisableClick = (service: PaidServiceAvailability) => {
    if (!canDisable(service.status)) {
      return;
    }
    setModalView({ type: "confirmDisable", service });
  };

  const handleBackToList = () => {
    setModalView({ type: "list" });
  };

  const handleConfirm = (
    mutation: typeof enablePaidService | typeof disablePaidService,
    service: PaidServiceAvailability,
  ) => {
    mutation.mutate(
      {
        studentId: student.id,
        paidServiceId: service.id,
        sectionId,
      },
      {
        onSuccess: () => {
          showSuccessMessage("有料サービス設定を変更しました");
          onClose();
        },
        onError: () => {
          showErrorMessage("有料サービス設定の変更に失敗しました");
        },
      },
    );
  };

  const renderModalContent = () => {
    switch (modalView.type) {
      case "confirmEnable":
        return (
          <ConfirmView
            type="enable"
            service={modalView.service}
            studentName={student.fullName}
            isUpdating={isUpdating}
            isPending={enablePaidService.isPending}
            onClose={onClose}
            onBack={handleBackToList}
            onConfirm={() =>
              handleConfirm(enablePaidService, modalView.service)
            }
          />
        );
      case "confirmDisable":
        return (
          <ConfirmView
            type="disable"
            service={modalView.service}
            studentName={student.fullName}
            isUpdating={isUpdating}
            isPending={disablePaidService.isPending}
            onClose={onClose}
            onBack={handleBackToList}
            onConfirm={() =>
              handleConfirm(disablePaidService, modalView.service)
            }
          />
        );
      case "list":
        return (
          <ListView
            student={student}
            paidServiceAvailabilities={paidServiceAvailabilities}
            isLoading={isLoading}
            onClose={onClose}
            onEnableClick={handleEnableClick}
            onDisableClick={handleDisableClick}
          />
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className={styles.modal}>
      {renderModalContent()}
    </Modal>
  );
};
